import { useQuery } from '@apollo/client/react'
import { Query, ScoContentPermissions, ScoUser } from '@src/graphql-types'
import { gql } from '@src/lib/gql'
import { useEffect } from 'react'

type UseCurrentUserArgs = {
  fragment?: () => any
}

type UseCurrentUserValue = {
  currentUser?: ScoUser
  currentUserLoading?: boolean
  currentUserError?: any
  refetchCurrentUser: () => any
  currentUserHasContentPermission: (permission: keyof ScoContentPermissions) => boolean
}

const CURRENT_USER_QUERY = gql`
  query {
    currentUser {
      id
      userId
      userToken
      createdAt
      slug
      firstName
      lastName
      fullName
      displayName
      bio
      shortBio
      title
      location
      locationCity
      locationState
      locationCountryKey
      locationPostalCode
      emailNotificationSchedule
      company {
        id
        annualRevenueKey
        companyId
        elevatorPitch
        fundingRaisedKey
        fundingStageKey
        fundingGoalInDollars
        headcountKey
        industryKey
        industryKeys
        location
        locationCity
        locationState
        locationCountryKey
        locationPostalCode
        logoUrl
        name
        stageKey
        url
        yearFounded
        pitchDeckNote
        pitchDeckUrl
        profileVisibilityKey
      }
      defaultCouponCode
      email
      emailConfirmedAt
      isEmailConfirmed
      phone
      pictureUrl
      coverPhotoUrl
      coverPhotoXInPercent
      coverPhotoYInPercent
      isAdmin
      ## CRM gate. startups3 must deploy this field before s5: an unknown field fails validation
      ## for the WHOLE operation, so currentUser would be null everywhere until the API has it.
      isScoAdmin
      isAdvisor
      isBeancounterAdmin
      isFinanceAdmin
      isGuest
      isFounderCommunityMember
      isFounderCommunityUser
      isFounderGroupsMember
      isFounderCommunityUser
      isStartupsUnlimitedMember
      isRegistered
      isPasswordSet
      canUserEdit
      canUserDelete
      fgAdvisor {
        id
        fullName
        email
        photoUrl
      }
      fgAccountManager {
        id
        fullName
        email
        photoUrl
      }
      fgOnboardingCallUrl
      contentPermissions {
        id
        canAccessChat
        canAccessPitchDeckAuditor
        canAccessMemberMatching
        canAccessInvestorMatching
        canAccessVideos
        canAccessCourses
        canAccessFGAccountManager
        canAccessFGAdvisor
        canAccessFGDashboard
        canAccessFGInvestorContacts
        canAccessFGRequests
        canAccessFGMemberDirectory
        canAccessFGOfficeHours
        canAccessFGDiscussions
        canAccessFGBasicEvents
        canAccessFGPremiumEvents
        canAccessFGWorkshops
        canAccessFundingAccelerator
        canAccessPremiumBenefits
        canActivateBizplan
        canActivateFundable
        canActivateLaunchrock
        canAccessAdvisorTeam
        canAccessGetCustomers
        sessionEarningsFeeAsPercent
      }
      hasAdvisorBoard
      advisorProfile {
        id
        advisorProfileId
        statusKey
        isApproved
      }
      fgAirtableMemberRecordIdentifier
      remainingInvestorContactRequestsCount
      remainingMemberChatsCount
      activeSprintSlug
      activeSprintNotes
      founderNeeds
      founderNeedsKeys ## array of GroupTopic.key's
      founderNeedsOther ## the textarea for "what's your best outcome"
      preferredInteractions
      preferredInteractionsKeys ## array of GroupInteractionTypes.key's
      fgLaunchedAt
      subscriptions {
        id
        subscriptionId
        activatedAt
        cardLabel
        isActive
        currentPeriodStartedAt
        currentPeriodEndsAt
        trialEndedAt
        trialStartedAt
        nextAssessmentAt
        nextAssessmentAmountInCents
        productId
        state
        product {
          id
          productId
          name
          specialTermsNote
          termId
          effectiveTermId
        }
      }
      userPreferences {
        id
        preferenceKey
        preferenceValueAsJson
      }
      profileVisibilityKey
      investorSearches {
        id
        pagination {
          itemCount
        }
      }
      userSearches {
        id
        pagination {
          itemCount
        }
      }
    }
  }
`

export const useCurrentUser = (args: UseCurrentUserArgs = {}): UseCurrentUserValue => {
  const query = useQuery<Query>(CURRENT_USER_QUERY, {
    ssr: true,
    pollInterval: 1 * 60 * 1000, // refetch every 1 minute to prevent multi-session state shifting
  })

  const currentUser = query.data?.currentUser || null

  // WebSocket auth uses the session cookie on the upgrade request (startups3).
  // Clear any legacy userId stored as sco:auth:websocketToken — raw userId is no longer accepted.
  useEffect(() => {
    try {
      localStorage.removeItem('sco:auth:websocketToken')
    } catch {
      // ignore (SSR / private mode)
    }
  }, [currentUser?.userId])

  return {
    currentUser,
    currentUserLoading: (query.loading && !currentUser) || null,
    currentUserError: query.error || null,
    currentUserHasContentPermission: (permission: keyof ScoContentPermissions) => {
      return !!currentUser?.contentPermissions?.[permission]
    },
    refetchCurrentUser: query.refetch || null,
  }
}
